import { Button, ButtonProps, notify } from '@billy/design-system'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { SpecificState } from '../../../types/reduxSaga-deprecated'
import { APIError, getCurrentLocale, getRawRequest } from '../../../utils'
import { formatDateRangePeriod } from '../utils'

export type DownloadLinesButtonProps = ButtonProps

export const DownloadLinesButton = ({ ...rest }: DownloadLinesButtonProps) => {
  const { t } = useTranslation()

  const account = useSelector((state: SpecificState) => state.app.account)
  const organizationId = useSelector((state: SpecificState) => state.app.organization)?.id || ''
  const fiscalYearEndMonth = useSelector((state: SpecificState) => state.app.organization)?.fiscalYearEndMonth ?? 12
  const filters = useSelector((state: SpecificState) => state.bankReconciliation.filters)

  const handleDownloadLines = useCallback(async () => {
    if (!account || !organizationId) {
      console.warn('No account or organization provided')
      return
    }

    try {
      const locale = getCurrentLocale()
      const { fiscalYear, dateRange } = filters
      const { from, to } = formatDateRangePeriod(fiscalYear, fiscalYearEndMonth, dateRange)

      const params = new URLSearchParams({
        accountId: String(account.id),
        isReconciled: 'false',
      })

      if (from) {
        params.append('minEntryDate', from)
      }

      if (to) {
        params.append('maxEntryDate', to)
      }

      const downloadResponse = await getRawRequest(
        `/organizations/${organizationId}/bankLines/download?${params.toString()}`,
        {
          'accept-language': locale,
        },
      )

      const blob = await downloadResponse.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = 'bank-lines.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 200)
    } catch (error) {
      if (error instanceof APIError && error.body?.errorCode === 'BANK_LINES_CSV_EXPORT_LIMIT_EXCEEDED') {
        notify({
          id: NotificationKeys.BankReconciliationDownloadLinesError,
          message: t('bankreconciliation.download_too_many_lines'),
          variant: 'error',
        })
      } else {
        notify({
          id: NotificationKeys.BankReconciliationDownloadLinesError,
          message: t('error_saving'),
          variant: 'error',
        })
      }
    }
  }, [account, filters, fiscalYearEndMonth, organizationId, t])

  return (
    <div data-tip={t('bankreconciliation.download_lines.trigger_tooltip')}>
      <Button {...rest} icon="download" onClick={handleDownloadLines} size="sm" variant="text" />
    </div>
  )
}
